import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Scroll, Save } from "lucide-react";

/**
 * LineageEditor — provider-only form to capture lineage / tradition /
 * teacher / temple / years initiated / certifications. Embedded inside the
 * Edit Profile drawer/page; persists to PATCH /api/profiles/me/lineage.
 *
 * Props:
 *   - profile (current provider profile, used to prefill)
 *   - onSaved (callback fired with updated profile)
 */
export default function LineageEditor({ profile, onSaved }) {
  const [form, setForm] = useState({
    tradition: profile?.tradition || "",
    teacher: profile?.teacher || "",
    yearsInitiated: profile?.yearsInitiated ?? "",
    temple: profile?.temple || "",
    lineageStatement: profile?.lineageStatement || "",
    certifications: (profile?.certifications || []).join("\n"),
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        tradition: form.tradition.trim() || null,
        teacher: form.teacher.trim() || null,
        yearsInitiated: form.yearsInitiated === "" ? null : Number(form.yearsInitiated),
        temple: form.temple.trim() || null,
        lineageStatement: form.lineageStatement.trim() || null,
        certifications: form.certifications
          .split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.patch("/profiles/me/lineage", body);
      toast.success("Lineage saved");
      onSaved?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save lineage");
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, children }) => (
    <label className="block space-y-1.5">
      <span className="text-[11px] uppercase tracking-[0.16em] text-foreground/55">{label}</span>
      {children}
    </label>
  );

  return (
    <div className="space-y-5 border border-secondary/20 bg-card/40 rounded-sm p-4" data-testid="lineage-editor">
      <div className="flex items-center gap-2 text-secondary/85">
        <Scroll size={14} strokeWidth={1.5} />
        <span className="label-mystic">Lineage &amp; Tradition</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Tradition">
          <input
            value={form.tradition}
            onChange={(e) => setForm({ ...form, tradition: e.target.value })}
            placeholder="Yoruba Ifá · Vodou · Quimbanda · Ceremonial..."
            className="w-full bg-input border border-secondary/20 px-3 py-2 rounded-sm focus:outline-none focus:border-secondary text-sm"
            data-testid="lineage-tradition"
          />
        </Field>
        <Field label="Teacher / Mentor">
          <input
            value={form.teacher}
            onChange={(e) => setForm({ ...form, teacher: e.target.value })}
            placeholder="Iyalorisha Maria... · Babalawo Ifabunmi..."
            className="w-full bg-input border border-secondary/20 px-3 py-2 rounded-sm focus:outline-none focus:border-secondary text-sm"
            data-testid="lineage-teacher"
          />
        </Field>
        <Field label="Years Initiated">
          <input
            type="number" min={0} max={120}
            value={form.yearsInitiated}
            onChange={(e) => setForm({ ...form, yearsInitiated: e.target.value })}
            placeholder="12"
            className="w-full bg-input border border-secondary/20 px-3 py-2 rounded-sm focus:outline-none focus:border-secondary text-sm"
            data-testid="lineage-years"
          />
        </Field>
        <Field label="Temple / House">
          <input
            value={form.temple}
            onChange={(e) => setForm({ ...form, temple: e.target.value })}
            placeholder="Ilé Asho Funfun..."
            className="w-full bg-input border border-secondary/20 px-3 py-2 rounded-sm focus:outline-none focus:border-secondary text-sm"
            data-testid="lineage-temple"
          />
        </Field>
      </div>

      <Field label="Lineage Statement">
        <textarea
          rows={4}
          value={form.lineageStatement}
          onChange={(e) => setForm({ ...form, lineageStatement: e.target.value })}
          placeholder="Initiated 2008 under Ilé Asho Funfun. Trained in Ifá divination by Babalawo Ifabunmi. Caretaker of three orisha shrines..."
          className="w-full bg-input border border-secondary/20 px-3 py-2 rounded-sm focus:outline-none focus:border-secondary text-sm"
          data-testid="lineage-statement"
        />
      </Field>

      <Field label="Certifications (one per line)">
        <textarea
          rows={3}
          value={form.certifications}
          onChange={(e) => setForm({ ...form, certifications: e.target.value })}
          placeholder={"Certified Reiki Master (Usui), 2014\nIngeniero Ceremonial, IFEN 2019"}
          className="w-full bg-input border border-secondary/20 px-3 py-2 rounded-sm focus:outline-none focus:border-secondary text-sm font-mono"
          data-testid="lineage-certifications"
        />
      </Field>

      <button
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-sm uppercase tracking-wider text-xs disabled:opacity-50"
        data-testid="lineage-save"
      >
        <Save size={14} strokeWidth={1.5} />
        {saving ? "Saving..." : "Save Lineage"}
      </button>
    </div>
  );
}
